interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

// Base skeleton styles
const baseStyles = 'animate-pulse bg-gradient-to-r from-elevated via-border to-elevated bg-[length:200%_100%]';

const variantStyles = {
  text: 'rounded',
  circular: 'rounded-full',
  rectangular: 'rounded-none',
  rounded: 'rounded-lg',
};

const animationStyles = {
  pulse: 'animate-pulse',
  wave: 'animate-shimmer',
  none: '',
};

export default function Skeleton({
  className = '',
  variant = 'text',
  width,
  height,
  animation = 'pulse',
}: SkeletonProps) {
  const style: React.CSSProperties = {
    width: width ?? '100%',
    height: height ?? (variant === 'text' ? '1em' : variant === 'circular' ? '40px' : '100px'),
  };

  return (
    <div
      className={`
        ${baseStyles}
        ${variantStyles[variant]}
        ${animationStyles[animation]}
        ${className}
      `}
      style={style}
      aria-hidden="true"
    />
  );
}

// Predefined skeleton components for common use cases

export function SkeletonCard({ className = '' }: { className?: string }) {
  return (
    <div className={`bg-surface border border-border rounded-lg p-4 ${className}`}>
      <Skeleton variant="rounded" height={24} width="60%" className="mb-3" />
      <Skeleton variant="text" height={16} width="100%" className="mb-2" />
      <Skeleton variant="text" height={16} width="80%" />
    </div>
  );
}

export function SkeletonList({ count = 3, className = '' }: { count?: number; className?: string }) {
  return (
    <div className={className}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 py-3">
          <Skeleton variant="circular" width={40} height={40} />
          <div className="flex-1">
            <Skeleton variant="text" height={16} width="40%" className="mb-2" />
            <Skeleton variant="text" height={12} width="70%" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonTable({ rows = 5, columns = 4, className = '' }: { rows?: number; columns?: number; className?: string }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {/* Header */}
      <div className="flex gap-2">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={32} className="flex-1" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-2">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} variant="rounded" height={40} className="flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonButton({ className = '' }: { className?: string }) {
  return (
    <Skeleton variant="rounded" height={40} width={120} className={className} />
  );
}

export function SkeletonAvatar({ size = 40, className = '' }: { size?: number; className?: string }) {
  return (
    <Skeleton variant="circular" width={size} height={size} className={className} />
  );
}

export function SkeletonText({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton 
          key={i} 
          variant="text" 
          height={16} 
          width={i === lines - 1 ? '60%' : '100%'} 
        />
      ))}
    </div>
  );
}

export function SkeletonImage({ className = '' }: { className?: string }) {
  return (
    <Skeleton variant="rounded" height={200} className={className} />
  );
}

export function SkeletonScreen({ className = '' }: { className?: string }) {
  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <Skeleton variant="rounded" height={32} width={200} />
        <Skeleton variant="rounded" height={32} width={100} />
      </div>
      <Skeleton variant="rounded" height={400} />
      <div className="flex gap-2">
        <Skeleton variant="rounded" height={48} width={80} />
        <Skeleton variant="rounded" height={48} width={80} />
        <Skeleton variant="rounded" height={48} width={80} />
      </div>
    </div>
  );
}

export function SkeletonEvent({ className = '' }: { className?: string }) {
  return (
    <div className={`bg-surface border border-border rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <Skeleton variant="circular" width={40} height={40} />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <Skeleton variant="text" height={18} width={150} />
            <Skeleton variant="rounded" height={24} width={60} />
          </div>
          <Skeleton variant="text" height={14} width="100%" className="mb-1" />
          <Skeleton variant="text" height={14} width="80%" />
        </div>
      </div>
    </div>
  );
}
